import { roundFix } from 'utils/numbers';
import { lastElement, getFilledArray } from 'utils/arrays';
import getDefaultSharedSeatsRatio from 'services/inSite/sharedSeatsRatio';

const getHeadcount = (lenght, initialHeadcount, changeArray) => {
  return new Array(lenght).fill(0).reduce(
    (headcountAcc, _, index) => ({
      baseHeadcount: headcountAcc.totalHeadcount,
      totalHeadcount: roundFix(headcountAcc.totalHeadcount * (1 + changeArray[index] / 100)),
    }),
    { baseHeadcount: initialHeadcount, totalHeadcount: initialHeadcount },
  );
};

const getHeadcountBreakdown = (
  totalHeadcount,
  officeRatio,
  remoteRatio,
  hybridRatio,
  daysInOffice,
  sharedSeatsRatio,
) => {
  const remote = roundFix((totalHeadcount * remoteRatio) / 100);
  const hybrid = roundFix((totalHeadcount * hybridRatio) / 100);
  const office = roundFix(totalHeadcount - remote - hybrid);
  const seats = office + roundFix(hybrid / getDaysInOfficeRatio(daysInOffice, sharedSeatsRatio));

  return { office, remote, hybrid, seats };
};

export const getDaysInOfficeRatio = (days, sharedSeatsRatio = getDefaultSharedSeatsRatio()) => {
  const selectedRatio = sharedSeatsRatio.find((item) => item.day === days);
  if (selectedRatio) return selectedRatio.ratio;
  else throw Error('getDaysInOfficeRatio function passed a non existing days param ' + days);
};

export const lastHeadcount = (growth) =>
  growth && growth.length && lastElement(growth).totalHeadcount;

export const initGrowth = (props) => {
  const {
    startingYear,
    years,
    headcount,
    officeRatio,
    remoteRatio,
    hybridRatio,
    daysInOffice,
    defaultGrowth,
    sharedSeatsRatio,
  } = props;

  return new Array(years + 1).fill(0).map((_, index) => {
    const year = startingYear + index;
    const change = defaultGrowth;

    const { baseHeadcount, totalHeadcount } = getHeadcount(
      index + 1,
      headcount,
      getFilledArray(index + 1, defaultGrowth),
    );

    const { office, remote, hybrid, seats } = getHeadcountBreakdown(
      totalHeadcount,
      officeRatio,
      remoteRatio,
      hybridRatio,
      daysInOffice,
      sharedSeatsRatio,
    );

    return {
      year,
      change,
      office,
      remote,
      hybrid,
      baseHeadcount,
      totalHeadcount,
      seats,
    };
  });
};

export const updateGrowthParams = (
  headcount,
  officeRatio,
  remoteRatio,
  hybridRatio,
  daysInOffice,
  growth,
  sharedSeatsRatio,
) =>
  growth.map((growth_row, index) => {
    const year = growth_row.year;
    const change = growth_row.change;

    const { baseHeadcount, totalHeadcount } = getHeadcount(
      index + 1,
      headcount,
      growth.map((growth_headcount) => growth_headcount.change),
    );

    const { office, remote, hybrid, seats } = getHeadcountBreakdown(
      totalHeadcount,
      officeRatio,
      remoteRatio,
      hybridRatio,
      daysInOffice,
      sharedSeatsRatio,
    );

    return {
      year,
      change,
      office,
      remote,
      hybrid,
      baseHeadcount,
      totalHeadcount,
      seats,
    };
  });

export const updateGrowthStartingYear = (newStartingYear, growth) =>
  growth.map((growth_row, index) => ({
    ...growth_row,
    year: newStartingYear + index,
  }));

export const updateGrowthLeaseTermInYears = (
  leaseTermInYears,
  officeRatio,
  remoteRatio,
  hybridRatio,
  daysInOffice,
  defaultGrowth,
  growth,
  sharedSeatsRatio,
) => {
  if (growth.length > leaseTermInYears) return growth.slice(0, leaseTermInYears + 1);
  const last_growth = lastElement(growth);
  const new_growth = initGrowth({
    startingYear: last_growth.year + 1,
    years: leaseTermInYears - growth.length,
    headcount: last_growth.totalHeadcount,
    officeRatio: officeRatio,
    remoteRatio: remoteRatio,
    hybridRatio: hybridRatio,
    daysInOffice: daysInOffice,
    defaultGrowth: defaultGrowth,
    sharedSeatsRatio: sharedSeatsRatio,
  });

  return growth.concat(new_growth);
};

export const updateGrowthDefaultChange = (
  newDefaultChange,
  headcount,
  officeRatio,
  remoteRatio,
  hybridRatio,
  daysInOffice,
  growth,
  sharedSeatsRatio,
) =>
  growth.map((growth_row, index) => {
    const year = growth_row.year;
    const change = newDefaultChange;

    const { baseHeadcount, totalHeadcount } = getHeadcount(
      index + 1,
      headcount,
      getFilledArray(index + 1, newDefaultChange),
    );

    const { office, remote, hybrid, seats } = getHeadcountBreakdown(
      totalHeadcount,
      officeRatio,
      remoteRatio,
      hybridRatio,
      daysInOffice,
      sharedSeatsRatio,
    );

    return {
      ...growth_row,
      year,
      change,
      office,
      remote,
      hybrid,
      baseHeadcount,
      totalHeadcount,
      seats,
    };
  });

export const updateGrowthChange = (props) => {
  const {
    newChange,
    changeIndex,
    headcount,
    officeRatio,
    remoteRatio,
    hybridRatio,
    daysInOffice,
    growth,
    sharedSeatsRatio,
  } = props;

  return growth.map((growth_row, index) => {
    const getChange = (idx) => Number(changeIndex === idx ? newChange : growth[idx].change);

    const year = growth_row.year;
    const change = getChange(index);

    const { baseHeadcount, totalHeadcount } = getHeadcount(
      index + 1,
      headcount,
      growth.map((_, idx) => getChange(idx)),
    );

    const { office, remote, hybrid, seats } = getHeadcountBreakdown(
      totalHeadcount,
      officeRatio,
      remoteRatio,
      hybridRatio,
      daysInOffice,
      sharedSeatsRatio,
    );

    return {
      ...growth_row,
      year,
      change,
      office,
      remote,
      hybrid,
      baseHeadcount,
      totalHeadcount,
      seats,
    };
  });
};

export const updateGrowthHeadcount = (props) => {
  const {
    newHeadcount,
    changeIndex,
    headcount,
    officeRatio,
    remoteRatio,
    hybridRatio,
    daysInOffice,
    growth,
    sharedSeatsRatio,
  } = props;

  let rate = 0;
  if (Number(growth[changeIndex].baseHeadcount)) {
    rate = 100 * (newHeadcount / Number(growth[changeIndex].baseHeadcount) - 1);
  }

  return updateGrowthChange({
    newChange: rate,
    changeIndex,
    headcount,
    officeRatio,
    remoteRatio,
    hybridRatio,
    daysInOffice,
    growth,
    sharedSeatsRatio,
  });
};
